import { useCompleteOnboardingMutation } from '@/app/redux/endpoints/user';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { useUser } from '@/app/redux/hooks/user';
import FeatureTable from '@/common/ui/FeatureTable';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { ProIcon } from '@/common/ui/UpgradeButton';
import { notifyZapier } from '@/common/utils/zapier';
import {
  ArrowSquareOutIcon,
  BellRingingIcon,
  CheckCircleIcon,
  NewspaperClippingIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { Alert, AlertDescription, AlertTitle } from '@shadcn/components/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@shadcn/components/alert-dialog';
import { Button } from '@shadcn/components/button';
import { Checkbox } from '@shadcn/components/checkbox';
import { Separator } from '@shadcn/components/separator';
import dayjs from 'dayjs';
import { FacebookIcon, InstagramIcon } from 'lucide-react';
import { useState } from 'react';

const day11 = dayjs().add(11, 'days');
const day14 = dayjs().add(14, 'days');

const steps = [
  {
    title: 'Welcome aboard!',
    description: (
      <span>
        You've got full access to{' '}
        <span className="inline-flex items-center justify-center gap-1">
          <span className="font-semibold text-foreground/80">
            Spellcaster Pro
          </span>
          <ProIcon size={3.5} />
        </span>{' '}
        features for the next 14 days. Here's how it works...
      </span>
    ),
    renderContent: () => (
      <div className="text-sm flex items-center gap-8 mb-2 px-4">
        <div className="flex flex-col justify-center w-2/5 p-6 bg-accent/70 rounded-lg h-[90%]">
          <div className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <CheckCircleIcon
                className="size-5 text-green-600"
                weight="fill"
              />
              <Separator orientation="vertical" className="h-10 w-px mt-2" />
            </div>
            <div className="flex flex-col pb-8">
              <span className="text-base font-medium">Today</span>
              <span className="text-xs text-muted-foreground">
                Full access to all Spellcaster Pro features.
              </span>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <BellRingingIcon className="size-5 text-blue-600" weight="bold" />
              <Separator orientation="vertical" className="h-14 w-px mt-2" />
            </div>
            <div className="flex flex-col pb-8">
              <div className="flex items-baseline gap-2">
                <span className="text-base font-medium">
                  {day11.format('D MMM')}
                </span>
                <span className="text-xs text-muted-foreground">Day 11</span>
              </div>
              <span className="text-xs text-muted-foreground">
                We'll send you an email reminder 3 days before your Spellcaster
                trial ends.
              </span>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <ProIcon size={5} />
            </div>
            <div className="flex flex-col">
              <div className="flex items-baseline gap-2">
                <span className="text-base font-medium">
                  {day14.format('D MMM')}
                </span>
                <span className="text-xs text-muted-foreground">Day 14</span>
              </div>
              <span className="text-xs text-muted-foreground">
                Choose to continue with one of our Spellcaster plans or continue
                to enjoy Spellcaster's free features (plans start at $99 pa!).
              </span>
            </div>
          </div>
        </div>

        <div className="w-3/5">
          <FeatureTable />
        </div>
      </div>
    ),
  },
  {
    title: 'Get the most out of your Spellcaster trial',
    description: 'Getting your first learner set up is as easy as...',
    renderContent: () => (
      <div className="grid place-items-center">
        <img
          src="/ui/happyPath.png"
          alt="1. Add Learners, 2. Get learner logged in, 3. Play a session"
          className="px-6 max-h-[50vh] object-contain"
        />
      </div>
    ),
  },
  {
    title: 'Get the lastest Spellcaster updates',
    description:
      'Spellcaster is constantly being updated and improved—here are the best ways to learn about news, upates and announcements',
    renderContent: (
      marketingOptIn: boolean,
      setMarketingOptIn: (optIn: boolean) => void,
      loading: boolean,
    ) => (
      <div className="flex flex-col gap-3">
        <a
          href="https://instagram.com/rupert_spellcaster/"
          target="_blank"
          rel="noopener noreferrer"
          className="group border border-foreground/20 hover:border-primary rounded-lg px-6 py-4 flex items-center justify-between gap-4 transition-all duration-200 hover:shadow-sm cursor-pointer"
        >
          <div className="flex items-center gap-4">
            <div className="p-2 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 text-white">
              <InstagramIcon className="size-5" />
            </div>
            <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
              Follow us on Instagram
            </span>
          </div>
          <ArrowSquareOutIcon className="size-5 text-muted-foreground group-hover:text-primary transition-colors" />
        </a>

        <a
          href="https://www.facebook.com/groups/1206209870371402/"
          target="_blank"
          rel="noopener noreferrer"
          className="group border border-foreground/20 hover:border-primary rounded-lg px-6 py-4 flex items-center justify-between gap-4 transition-all duration-200 hover:shadow-sm cursor-pointer"
        >
          <div className="flex items-center gap-4">
            <div className="p-2 rounded-full bg-blue-600 text-white">
              <FacebookIcon className="size-5" />
            </div>
            <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
              Join our Spellcaster Users Group on Facebook
            </span>
          </div>
          <ArrowSquareOutIcon className="size-5 text-muted-foreground group-hover:text-primary transition-colors" />
        </a>

        <div
          className="group border border-foreground/20 hover:border-primary rounded-lg px-6 py-4 flex items-center gap-4 transition-all duration-200 hover:shadow-sm cursor-pointer"
          onClick={() => setMarketingOptIn(!marketingOptIn)}
        >
          <div className="flex items-center gap-4 flex-1">
            <div className="p-2 rounded-full bg-primary text-primary">
              <NewspaperClippingIcon className="size-5 text-white" />
            </div>
            <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
              Subscribe to our newsletter
            </span>
          </div>
          <Checkbox
            className="mr-1"
            checked={marketingOptIn}
            onCheckedChange={(checked) => setMarketingOptIn(!!checked)}
            disabled={loading}
          />
        </div>
      </div>
    ),
  },
];

export default function OnboardingDialogue() {
  const { user } = useUser();
  const { organisation } = useOrganisation();

  const [completeOnboarding] = useCompleteOnboardingMutation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const onboardingComplete = user?.onboardingCompleted ?? false;

  const handleFinish = async () => {
    if (!user || !organisation) {
      console.error('Cannot complete onboarding, user or org is missing');
      setError(true);
      return;
    }

    try {
      setLoading(true);
      await completeOnboarding({ userId: user.uid, marketingOptIn }).unwrap();
      notifyZapier({
        hook: 'onboarding',
        userId: user.uid,
        organisationId: organisation.id,
        organisationName: organisation.name,
        name: user.name,
        email: user.email,
        role: user.onboardingInfo.role ?? 'Unknown',
        referrer: user.onboardingInfo.referrer ?? 'Unknown',
        numStudents: user.onboardingInfo.numStudents ?? 'Unknown',
        marketingOptIn,
      });
    } catch (error) {
      console.error('Error completing onboarding:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={!onboardingComplete}>
      <AlertDialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <AlertDialogHeader className="space-y-1">
          <AlertDialogTitle>{steps[stepIdx].title}</AlertDialogTitle>
          <AlertDialogDescription>
            {steps[stepIdx].description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && (
          <Alert variant="destructive">
            <WarningCircleIcon className="size-4 ml-2" />
            <AlertTitle>Failed to complete onboarding</AlertTitle>
            <AlertDescription>{UNKNOWN_ERROR_MESSAGE}</AlertDescription>
          </Alert>
        )}

        <div className="overflow-y-auto min-h-0">
          {steps[stepIdx].renderContent(
            marketingOptIn,
            setMarketingOptIn,
            loading,
          )}
        </div>

        <AlertDialogFooter>
          {stepIdx > 0 && (
            <Button
              variant="outline"
              onClick={() => setStepIdx((i) => i - 1)}
              disabled={loading}
            >
              Back
            </Button>
          )}
          <Button
            loading={loading}
            onClick={() =>
              stepIdx === steps.length - 1
                ? handleFinish()
                : setStepIdx((i) => i + 1)
            }
          >
            {stepIdx === steps.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
